import { Card, Checkbox, Space, Tooltip, Spin } from 'antd'
import { GripVertical } from 'lucide-react'
import { LineItemImage } from './LineItemImage'
import { LineItemCode } from './LineItemCode'
import { LineItemTitle } from './LineItemTitle'
import { LineItemDescription } from './LineItemDescription'
import { LineItemSupplier } from './LineItemSupplier'
import { LineItemSupplierCode } from './LineItemSupplierCode'
import { LineItemLeadTime } from './LineItemLeadTime'
import { LineItemPricing } from './LineItemPricing'
import { MaterialThumbnails } from './MaterialThumbnails'
import { DuplicateButton } from '../Common/DuplicateButton'
import { GroupSelectionButton } from './GroupSelectionButton'
import { Group, LineItem } from '../../types/ffe'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { useFFEStore } from '../../store/ffeStore'
import { LineItemStatus } from './LineItemStatus'
import React from 'react'
import { MoreLineItemControls } from './MoreLineItemControls'

interface Props {
  item: LineItem
  group?: Group
  listeners: SyntheticListenerMap | undefined
}

const LineItemImageMemo = React.memo(LineItemImage)
const LineItemPricingMemo = React.memo(LineItemPricing)

export const LineItemCard = ({ item, group, listeners }: Props) => {
  const updateLineItem = useFFEStore((state) => state.updateLineItem)
  const updateGroup = useFFEStore((state) => state.updateGroup)
  const loadingLineItemIds = useFFEStore((state) => state.loadingLineItemIds)

  const handleUpdate = (updates: Partial<LineItem>) => {
    updateLineItem(item.id, updates)
  }

  const isSelectedAsAlternative = group?.selectedItemId === item.id
  const isLoading = loadingLineItemIds.includes(item.id)

  const loadingMessages = React.useMemo(
    () => [
      'Looking up supplier…',
      'Parsing product details…',
      'Analyzing product images…',
      'Estimating lead time…',
      'Calculating pricing…',
      'Finishing touches…',
    ],
    []
  )

  const [loadingMessageIndex, setLoadingMessageIndex] = React.useState(0)

  React.useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0)
      return
    }
    const id = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length)
    }, 3000)
    return () => clearInterval(id)
  }, [isLoading, loadingMessages.length])

  const changeSelectedAlternative = () => {
    if (!isSelectedAsAlternative) {
      // Select this item
      updateGroup(group!.id, { selectedItemId: item.id })
    } else {
      // Deselect this item
      updateGroup(group!.id, { selectedItemId: undefined })
    }
  }

  const isDeliveryItem = item.systemCategory === 'DELIVERY'

  return (
    <Card
      className="mb-4 transition-colors duration-500"
      extra={<Space>
        {group?.isAlternatives && (
          <span>
            <Tooltip title="The selected item in this alternatives group will be highlighted for the client and count towards the document total.">
              <Checkbox
                checked={isSelectedAsAlternative}
                onChange={changeSelectedAlternative}
                disabled={isLoading}
              >
                <span>Selected item</span>
              </Checkbox>
            </Tooltip>
          </span>
        )}
        <GroupSelectionButton
          itemId={item.id}
          currentGroupId={item.groupId}
        />
        <DuplicateButton
          disabled={isDeliveryItem || isLoading}
          item={item}
          group={group}
        />
        <MoreLineItemControls item={item} />
      </Space>}
      styles={{ body: { padding: 0 } }}
    >
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/60 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2">
              <Spin size="large" />
              <div className="text-xs text-gray-600">{loadingMessages[loadingMessageIndex]}</div>
            </div>
          </div>
        )}
        <div className={isLoading ? 'opacity-60 pointer-events-none' : ''}>
          <Space direction="vertical" className="w-full p-4 sm:p-4">
            <div className="flex items-start gap-4 flex-col sm:flex-row">
              <div className="hidden sm:flex mt-2">
                <button
                  {...listeners}
                  style={{ padding: '1em', margin: '-1em' }}
                  className="cursor-grab"
                  disabled={isLoading}
                >
                  <GripVertical className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              <div className="relative">
                <LineItemImageMemo item={item} onUpdate={handleUpdate} />
                <div className="absolute -right-2 -bottom-2">
                  <MaterialThumbnails item={item} />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2 justify-between">
                  <div className="flex gap">
                    <LineItemCode item={item} onUpdate={handleUpdate} />
                    <LineItemTitle item={item} onUpdate={handleUpdate} />
                  </div>
                  <div className="flex gap-2 items-center">
                    {!isDeliveryItem && (
                      <LineItemStatus item={item} onUpdate={handleUpdate} />
                    )}
                    
                  </div>
                </div>
                <LineItemDescription item={item} onUpdate={handleUpdate} />
                {!isDeliveryItem && (
                  <div className="flex gap md:gap-2 flex-col md:flex-row items-start md:items-center mt-2 md:mt-0">
                    <LineItemSupplier item={item} onUpdate={handleUpdate} />
                    <LineItemSupplierCode item={item} onUpdate={handleUpdate} />
                    <LineItemLeadTime item={item} onUpdate={handleUpdate} />
                  </div>
                )}
              </div>
            </div>
            <LineItemPricingMemo item={item} onUpdate={handleUpdate} />
          </Space>
        </div>
      </div>
    </Card>
  )
}
